import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../utils/api";
import { Colors } from "../utils/utils.colors";
import { PageSectionHeader } from "./subcomponents/PageSectionHeader";

type ActivityItem = {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  coverImage: string | null;
  category: string | null;
  createdAt: string;
};

const CategoryCarousel: React.FC<{ category: string; items: ActivityItem[] }> = ({ category, items }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.6;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: Colors.darkColor, margin: 0 }}>{category}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => scroll("left")} style={arrowBtnStyle} aria-label="Précédent">‹</button>
          <button onClick={() => scroll("right")} style={arrowBtnStyle} aria-label="Suivant">›</button>
        </div>
      </div>
      <div
        ref={scrollRef}
        style={{
          display: 'flex', gap: '1rem', overflowX: 'auto', scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch', paddingBottom: 8, scrollbarWidth: 'thin',
        }}
      >
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/articles/${item.slug}`}
            style={{
              flex: '0 0 280px', scrollSnapAlign: 'start',
              background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              overflow: 'hidden', textDecoration: 'none', color: 'inherit',
              border: '1px solid #eee', transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
          >
            {item.coverImage && (
              <div style={{ width: '100%', height: 160, overflow: 'hidden' }}>
                <img src={item.coverImage} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div style={{ padding: '1rem' }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#222', margin: '0 0 0.5rem', lineHeight: 1.4 }}>{item.title}</h4>
              {item.excerpt && (
                <p style={{ fontSize: 12, color: '#666', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {item.excerpt}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const arrowBtnStyle: React.CSSProperties = {
  width: 32, height: 32, borderRadius: '50%', border: `1px solid #ddd`,
  background: '#fff', cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  fontSize: 20, fontWeight: 700, color: '#555', lineHeight: 1,
};

const shimmerKeyframes = `
@keyframes carouselShimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`;

const shimmerStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '800px 100%',
  animation: 'carouselShimmer 1.5s ease-in-out infinite',
  borderRadius: 8,
};

const SkeletonCard: React.FC = () => (
  <div style={{ flex: '0 0 280px', scrollSnapAlign: 'start', borderRadius: 12, overflow: 'hidden', border: '1px solid #eee' }}>
    <div style={{ width: '100%', height: 160, ...shimmerStyle }} />
    <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ height: 16, width: '85%', ...shimmerStyle }} />
      <div style={{ height: 14, width: '100%', ...shimmerStyle }} />
      <div style={{ height: 14, width: '60%', ...shimmerStyle }} />
    </div>
  </div>
);

const CarouselSkeleton: React.FC = () => (
  <>
    {[1, 2, 3].map((cat) => (
      <div key={cat} style={{ marginBottom: '2.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ height: 22, width: 180, ...shimmerStyle }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', ...shimmerStyle }} />
            <div style={{ width: 32, height: 32, borderRadius: '50%', ...shimmerStyle }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    ))}
  </>
);

export const StudentActivitiesCarousel: React.FC = () => {
  const [grouped, setGrouped] = useState<Record<string, ActivityItem[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet("/contents", { type: "activity", limit: 200 })
      .then((res: any) => {
        const rows: ActivityItem[] = res.rows || [];
        const map: Record<string, ActivityItem[]> = {};
        for (const item of rows) {
          const cat = item.category || "Autres activités";
          if (!map[cat]) map[cat] = [];
          map[cat].push(item);
        }
        setGrouped(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categoryOrder = Object.keys(grouped).sort();

  if (!loading && categoryOrder.length === 0) {
    return null;
  }

  return (
    <div className="page-content has-global-padding is-layout-constrained is-layout-container">
      <style>{shimmerKeyframes}</style>
      <div className="wp-block-group is-layout-flow wp-block-group-is-layout-flow">
        <PageSectionHeader
          eyebrow="Activités étudiantes"
          title="Vie étudiante & activités"
          lead="Découvrez les activités organisées par nos étudiants, classées par catégories."
          align="center"
        />

        {loading ? (
          <CarouselSkeleton />
        ) : (
          categoryOrder.map((cat) => (
            <CategoryCarousel key={cat} category={cat} items={grouped[cat]} />
          ))
        )}
      </div>
    </div>
  );
};
